"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import { AlertCircle, ArrowRight, Zap } from "@/components/icons";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertBanner,
  AlertBannerContent,
  AlertBannerIcon,
  AlertBannerText,
  AlertBannerAction,
} from "@/components/ui/alert-banner";
import { PricingCard } from "./pricing-card";
import { BillingToggle } from "./billing-toggle";
import { FeatureTable } from "./feature-table";
import { FAQSection } from "@/components/platform/faq-section";
import { faqs, unifiedTiers } from "../data";
import type { BillingInterval } from "../data";

export function PricingContent() {
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>("monthly");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const handleBillingChange = useCallback((interval: BillingInterval) => {
    setCheckoutError(null);
    setBillingInterval(interval);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-center">
        <BillingToggle value={billingInterval} onChange={handleBillingChange} />
      </div>

      {checkoutError && (
        <AlertBanner variant="critical" layout="card">
          <AlertBannerContent>
            <AlertBannerIcon>
              <AlertCircle />
            </AlertBannerIcon>
            <AlertBannerText>{checkoutError}</AlertBannerText>
          </AlertBannerContent>
          <AlertBannerAction>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCheckoutError(null)}
              className="text-destructive-strong"
            >
              Dismiss
            </Button>
          </AlertBannerAction>
        </AlertBanner>
      )}

      <div className="grid gap-6 md:grid-cols-3">
        {unifiedTiers.map((tier) => (
          <PricingCard
            key={tier.id}
            tier={tier}
            billingInterval={billingInterval}
            onError={setCheckoutError}
          />
        ))}
      </div>

      <FeatureTable />

      <FAQSection items={faqs} />

      <div className="border border-border bg-background">
        <div className="flex items-center gap-2 border-b border-border bg-muted px-4 py-2">
          <Zap className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">
            Not Sure Which Plan?
          </span>
        </div>
        <div className="p-6 text-center">
          <h2 className="text-lg font-medium text-foreground">
            Talk to Our Team
          </h2>
          <div className="mt-4 flex justify-center gap-3">
            <Link
              href="/contact?source=pricing"
              className={buttonVariants({
                variant: "primary",
                className: "rounded-none px-6 py-2.5",
              })}
            >
              Schedule a Call
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="/help"
              className={buttonVariants({
                variant: "outline",
                className: "rounded-none px-5 py-2.5",
              })}
            >
              Browse Help Center
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
